import { UserPlus, UserCog } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import {
  LIMITS,
  type AccountView,
  type InvitationOutcome,
} from "../../../shared/contracts.js";
import { ROLES, type Role } from "../../../shared/domain.js";
import { isClientApiError } from "../../api/http.js";
import {
  accountMutationErrorMessage,
  useAssignRole,
  useCreateAccount,
  useRenameAccount,
  type CreateAccountVariables,
} from "../../api/accountQueries.js";
import { Button, useControlSize } from "../../ui/Button.js";
import { Dialog, DialogActions } from "../../ui/Dialog.js";
import { FormField, TextField } from "../../ui/FormField.js";
import { temporaryPasswordError } from "../auth/password-rules.js";
import { ROLE_LABELS } from "../cases/parts-actions/RecipientWarning.js";

/**
 * The account form, in two modes:
 *
 * - `create`: name, email and role for a new employee account, and an
 *   optional temporary password. Left blank, the server generates one;
 *   typed (owner decision 2026-09-17: the dealership's mail server purges
 *   the invitation emails, so the administrator hands the password over
 *   from their own work email for now), it is checked in place with the
 *   change-password page's rules and words and used verbatim. Either way
 *   the server emails it to the address and returns it once with the
 *   email's outcome; both go straight to `onTemporaryPassword` and never
 *   into the mutation result. The typed value lives in this component's
 *   state alone — it reaches the request through the hook's accessor, not
 *   as a mutation variable, so the mutation cache never holds it — and
 *   leaves the document when the dialog closes.
 * - `edit`: the display name and the operational role of an existing
 *   account. Each is its own route, so Save sends only what changed: the
 *   rename first (`PATCH …/name`), then the role (`PATCH …/role`) with the
 *   version the rename answered, so the second write is never stale because
 *   of the first; nothing changed → nothing is sent and the dialog closes
 *   saying so. Both carry the version the list showed so a concurrent
 *   change is refused (409 `STALE_WRITE`) rather than silently overwritten.
 *   The page passes the account as the list holds it *now* (resolved by id
 *   on every render), so after a refusal — which reloads the list — the next
 *   Save carries the fresh version and the fields start over from the name
 *   and role that actually apply. Two requests can also half-succeed: when
 *   the rename has committed and the role write then fails, the message
 *   says the name was saved and only the role is still to do — never the
 *   generic "nothing was changed", which the row behind the dialog would
 *   already contradict — and the next Save sends the role alone.
 *
 * Administration is a capability, not a role, and this form never offers
 * it: `isAdmin` and `active` are not fields here — the server would refuse
 * them anyway — and the person's *own* password is never set from here:
 * the temporary one is replaced at their first sign-in.
 */

export type AccountDialogMode =
  | Readonly<{ kind: "create" }>
  | Readonly<{ kind: "edit"; account: AccountView }>;

export interface AccountDialogProps {
  mode: AccountDialogMode | null;
  onClose: () => void;
  /** Create mode: receives the one-time password, the email's outcome and the new account. */
  onTemporaryPassword: (
    password: string,
    invitation: InvitationOutcome,
    user: AccountView,
  ) => void;
  /** Called after a successful save with a message for the page's status region. */
  onSaved: (message: string) => void;
}

const NAME_MAX = LIMITS.name.max;
const EMAIL_MAX = LIMITS.email.max;
/** A minimal shape check; the server's `emailSchema` is the authority. */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type FieldErrors = Partial<
  Record<"displayName" | "email" | "role" | "temporaryPassword", string>
>;

/** The hint under the optional temporary password field, in both dialogs that offer it. */
export const TEMPORARY_PASSWORD_HINT =
  "Leave blank to generate one. They replace it at their first sign-in.";

/** Trimmed, inner whitespace collapsed — what the server stores and compares. */
function normalizeName(displayName: string): string {
  return displayName.trim().replace(/\s+/g, " ");
}

function nameError(displayName: string): string | undefined {
  const name = normalizeName(displayName);
  if (name.length === 0) return "Enter the person's full name.";
  if (name.length > NAME_MAX) return `Use at most ${NAME_MAX} characters.`;
  return undefined;
}

function validateCreate(values: {
  displayName: string;
  email: string;
  role: Role | "";
  temporaryPassword: string;
}): FieldErrors {
  const errors: FieldErrors = {};
  const displayName = nameError(values.displayName);
  if (displayName) errors.displayName = displayName;
  const email = values.email.trim();
  if (email.length === 0)
    errors.email = "Enter the person's work email address.";
  else if (email.length > EMAIL_MAX || !EMAIL_SHAPE.test(email))
    errors.email = "Enter a valid email address, like name@dealership.com.";
  if (values.role === "") errors.role = "Choose the person's role.";
  const temporaryPassword = temporaryPasswordError(values.temporaryPassword);
  if (temporaryPassword) errors.temporaryPassword = temporaryPassword;
  return errors;
}

function serverFieldErrors(error: unknown): FieldErrors {
  if (!isClientApiError(error)) return {};
  if (error.status === 409 && error.code === "EMAIL_IN_USE") {
    return { email: "An account with that email address already exists." };
  }
  const [displayName] = error.fieldErrors["displayName"] ?? [];
  const [email] = error.fieldErrors["email"] ?? [];
  const [role] = error.fieldErrors["role"] ?? [];
  const [temporaryPassword] = error.fieldErrors["temporaryPassword"] ?? [];
  const mapped: FieldErrors = {};
  if (displayName) mapped.displayName = displayName;
  if (email) mapped.email = email;
  if (role) mapped.role = role;
  if (temporaryPassword) mapped.temporaryPassword = temporaryPassword;
  return mapped;
}

export function AccountDialog({
  mode,
  onClose,
  onTemporaryPassword,
  onSaved,
}: AccountDialogProps) {
  if (mode === null) return null;
  return mode.kind === "create" ? (
    <CreateAccountDialog
      onClose={onClose}
      onTemporaryPassword={onTemporaryPassword}
      onSaved={onSaved}
    />
  ) : (
    <EditAccountDialog
      account={mode.account}
      onClose={onClose}
      onSaved={onSaved}
    />
  );
}

function RoleSelect({
  id,
  value,
  onChange,
  error,
  hint,
}: {
  id: string;
  value: Role | "";
  onChange: (role: Role | "") => void;
  error: string | null;
  hint?: string;
}) {
  const size = useControlSize();
  return (
    <FormField id={id} label="Role" error={error} hint={hint}>
      {(control) => (
        <select
          {...control}
          className="text-input select-input"
          data-size={size}
          value={value}
          onChange={(event) => onChange(event.target.value as Role | "")}
        >
          <option value="">Choose a role</option>
          {ROLES.map((role) => (
            <option key={role} value={role}>
              {ROLE_LABELS[role]}
            </option>
          ))}
        </select>
      )}
    </FormField>
  );
}

/**
 * The optional temporary password, offered by the create form and the
 * reset confirmation alike. Shown in the clear as it is typed: there is no
 * confirmation field, the administrator is about to read this very value
 * off the reveal dialog and hand it over, and a masked field would only
 * let a typo through; `autoComplete="off"` keeps a password manager from
 * offering to keep someone else's temporary password under the
 * administrator's own sign-in. Validated by the caller with
 * {@link temporaryPasswordError} — blank is not an error.
 */
export function TemporaryPasswordField({
  id,
  value,
  onChange,
  error,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  error: string | null;
}) {
  return (
    <TextField
      id={id}
      label="Temporary password"
      name="temporaryPassword"
      type="text"
      optional
      autoComplete="off"
      autoCapitalize="off"
      autoCorrect="off"
      spellCheck={false}
      hint={TEMPORARY_PASSWORD_HINT}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      error={error}
    />
  );
}

function CreateAccountDialog({
  onClose,
  onTemporaryPassword,
  onSaved,
}: Omit<AccountDialogProps, "mode">) {
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role | "">("");
  /** What the administrator typed; blank means the server generates one. */
  const [temporaryPassword, setTemporaryPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const create = useCreateAccount({
    onTemporaryPassword,
    // Read at send time from this state, never handed to `mutate`.
    chosenTemporaryPassword: () =>
      temporaryPassword.length > 0 ? temporaryPassword : undefined,
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const errors = validateCreate({ displayName, email, role, temporaryPassword });
    setFieldErrors(errors);
    create.reset();
    if (Object.keys(errors).length > 0 || role === "") return;
    const input: CreateAccountVariables = {
      displayName: normalizeName(displayName),
      email: email.trim(),
      role,
    };
    create.mutate(input, {
      onSuccess: (user) => {
        onSaved(`${user.displayName}'s account was created.`);
        onClose();
      },
    });
  };

  const fromServer = serverFieldErrors(create.error);
  const showAlert =
    create.isError &&
    !fromServer.displayName &&
    !fromServer.email &&
    !fromServer.role &&
    !fromServer.temporaryPassword;

  return (
    <Dialog
      open
      onClose={onClose}
      title="New account"
      description="The person is emailed a temporary password, valid for 72 hours, to replace at their first sign-in."
      pending={create.isPending}
      size="small"
    >
      <form className="account-form" onSubmit={onSubmit} noValidate>
        {showAlert ? (
          <div role="alert" className="form-alert">
            {accountMutationErrorMessage(create.error)}
          </div>
        ) : null}
        <TextField
          id="account-name"
          label="Full name"
          name="displayName"
          autoComplete="off"
          maxLength={NAME_MAX}
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
          error={fieldErrors.displayName ?? fromServer.displayName ?? null}
        />
        <TextField
          id="account-email"
          label="Email"
          name="email"
          type="email"
          autoComplete="off"
          inputMode="email"
          maxLength={EMAIL_MAX}
          hint="Arrival emails go here when this person is the assigned advisor, technician, or a manager, foreman or dispatcher."
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          error={fieldErrors.email ?? fromServer.email ?? null}
        />
        <RoleSelect
          id="account-role"
          value={role}
          onChange={setRole}
          error={fieldErrors.role ?? fromServer.role ?? null}
          hint="Only Parts and Parts manager can create and change orders. Everyone can view, search and comment."
        />
        <TemporaryPasswordField
          id="account-temporary-password"
          value={temporaryPassword}
          onChange={setTemporaryPassword}
          error={fieldErrors.temporaryPassword ?? fromServer.temporaryPassword ?? null}
        />
        <DialogActions>
          <Button
            variant="secondary"
            disabled={create.isPending}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            busy={create.isPending}
            icon={<UserPlus />}
          >
            Create account
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

/**
 * What one Save did, for the page's status line: "Ana Advisor was renamed
 * to Ana Adviser and is now Shop foreman." — or just the half that happened.
 */
function savedMessage(
  previousName: string,
  saved: AccountView,
  renamed: boolean,
  reRoled: boolean,
): string {
  const rename = `${previousName} was renamed to ${saved.displayName}`;
  const role = `is now ${ROLE_LABELS[saved.role]}`;
  if (renamed && reRoled) return `${rename} and ${role}.`;
  if (renamed) return `${rename}.`;
  return `${saved.displayName} ${role}.`;
}

/**
 * The alert for a Save whose rename committed and whose role write then
 * failed: the name is a fact by now (the list has already reloaded with it),
 * so the message says so and points at the role alone. A stale refusal
 * keeps its own explanation.
 */
function roleAfterRenameMessage(savedName: string, error: unknown): string {
  const stale =
    isClientApiError(error) && error.status === 409 && error.code === "STALE_WRITE";
  return stale
    ? `The name was saved as ${savedName}, but the role was refused: this account changed after you opened it. The list has been reloaded — check it and try Save again.`
    : `The name was saved as ${savedName}, but the role change did not go through. Check the role and try Save again.`;
}

function EditAccountDialog({
  account,
  onClose,
  onSaved,
}: {
  account: AccountView;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const [displayName, setDisplayName] = useState(account.displayName);
  const [role, setRole] = useState<Role | "">(account.role);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  /** The name the last Save committed before its role write failed; null otherwise. */
  const [nameSavedBeforeFailure, setNameSavedBeforeFailure] = useState<string | null>(null);
  const rename = useRenameAccount();
  const assign = useAssignRole();

  // The list refetched under us (after a stale write, or another admin's
  // change) and the page handed us the account as it is now: each field
  // starts over from the value that actually applies, so "check it and try
  // again" shows what changed before the administrator chooses again. Two
  // effects, one per field: a rename's own refetch must not undo a role the
  // administrator has picked but not yet saved, and the other way round.
  useEffect(() => {
    setDisplayName(account.displayName);
  }, [account.id, account.displayName]);
  useEffect(() => {
    setRole(account.role);
  }, [account.id, account.role]);

  const pending = rename.isPending || assign.isPending;
  const error = rename.isError ? rename.error : assign.isError ? assign.error : null;

  /**
   * The rename first, then the role with the version the rename answered.
   * A refusal leaves the mutation's own error for the alert below and the
   * dialog open; the hooks have already asked for the list, so the fields
   * refresh from what the server holds. A role write that fails after the
   * rename committed is remembered as such, so the alert can say what did
   * happen.
   */
  const save = async (
    nextName: string,
    nextRole: Role,
    renamed: boolean,
    reRoled: boolean,
  ) => {
    let renamedTo: string | null = null;
    try {
      let saved = account;
      if (renamed) {
        saved = await rename.mutateAsync({
          userId: account.id,
          displayName: nextName,
          expectedVersion: account.version,
        });
        renamedTo = saved.displayName;
      }
      if (reRoled) {
        saved = await assign.mutateAsync({
          userId: account.id,
          role: nextRole,
          expectedVersion: saved.version,
        });
      }
      onSaved(savedMessage(account.displayName, saved, renamed, reRoled));
      onClose();
    } catch {
      // Shown from the mutation state — unless the rename had already
      // landed, in which case the alert says so instead.
      setNameSavedBeforeFailure(renamedTo);
    }
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (pending) return;
    rename.reset();
    assign.reset();
    setNameSavedBeforeFailure(null);
    const errors: FieldErrors = {};
    const nameProblem = nameError(displayName);
    if (nameProblem) errors.displayName = nameProblem;
    if (role === "") errors.role = "Choose the person's role.";
    setFieldErrors(errors);
    if (nameProblem || role === "") return;

    const nextName = normalizeName(displayName);
    const renamed = nextName !== account.displayName;
    const reRoled = role !== account.role;
    if (!renamed && !reRoled) {
      onSaved(`Nothing changed for ${account.displayName}.`);
      onClose();
      return;
    }
    void save(nextName, role, renamed, reRoled);
  };

  const fromServer = serverFieldErrors(error);
  const halfSaved = error !== null && nameSavedBeforeFailure !== null;
  const showAlert =
    error !== null && (halfSaved || (!fromServer.displayName && !fromServer.role));

  return (
    <Dialog
      open
      onClose={onClose}
      title="Edit account"
      description={`The name and role for ${account.displayName} (${account.email}). Their administration access does not change.`}
      pending={pending}
      size="small"
    >
      <form className="account-form" onSubmit={onSubmit} noValidate>
        {showAlert ? (
          <div role="alert" className="form-alert">
            {halfSaved
              ? roleAfterRenameMessage(nameSavedBeforeFailure, error)
              : accountMutationErrorMessage(error)}
          </div>
        ) : null}
        <TextField
          id="account-name"
          label="Full name"
          name="displayName"
          autoComplete="off"
          maxLength={NAME_MAX}
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
          error={fieldErrors.displayName ?? fromServer.displayName ?? null}
        />
        <RoleSelect
          id="account-role"
          value={role}
          onChange={setRole}
          error={fieldErrors.role ?? fromServer.role ?? null}
        />
        <DialogActions>
          <Button variant="secondary" disabled={pending} onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            busy={pending}
            icon={<UserCog />}
          >
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
